import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import appConfig from '../../config/appConfig';
import { api, apiRoutes } from '../../api';
import StepIndicator from './components/StepIndicator';
import Step1_PersonalInfo from './steps/Step1_PersonalInfo';
import Step2_LocationAndNeighborhoods from './steps/Step2_LocationAndNeighborhoods';
import Step3_Experience from './steps/Step3_Experience';
import Step4_Documents from './steps/Step5_Documents';
import Step5_Review from './steps/Step6_Review';
import { submitApplication, agentApplicantAuth } from './agentApplicationService';
import './AgentApplicationWizard.css';

const STEP_LABELS = ['اطلاعات', 'موقعیت', 'سابقه', 'مدارک', 'بررسی'];
const TOTAL_STEPS = 5;

// ── Inline login form shown when national_id already exists ──────────────────
function LoginPrompt({ nationalId, onLoginSuccess, onBack }) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async () => {
        if (!password.trim()) { setError('رمز عبور را وارد کنید'); return; }
        setError('');
        setLoading(true);
        try {
            // username might be national_id itself or the agt_ prefixed one — try national_id as username
            const res = await api.post(apiRoutes.auth.token, {
                username: nationalId,
                password,
            });
            localStorage.setItem(appConfig.auth.accessTokenKey, res.data.access);
            localStorage.setItem(appConfig.auth.refreshTokenKey, res.data.refresh);

            // fetch profile
            const profile = await api.get(apiRoutes.auth.user, {
                headers: { Authorization: `Bearer ${res.data.access}` },
            });
            localStorage.setItem('user_id', String(profile.data.id));
            localStorage.setItem('username', profile.data.username);
            localStorage.setItem('user_profile', JSON.stringify({
                first_name: profile.data.first_name || '',
                last_name: profile.data.last_name || '',
                phone: profile.data.phone || '',
                email: profile.data.email || '',
                national_id: profile.data.national_id || '',
                is_agent: profile.data.is_agent || false,
            }));
            onLoginSuccess();
        } catch (err) {
            // Try with agt_{nationalId} username as fallback
            try {
                const res2 = await api.post(apiRoutes.auth.token, {
                    username: `agt_${nationalId}`,
                    password,
                });
                localStorage.setItem(appConfig.auth.accessTokenKey, res2.data.access);
                localStorage.setItem(appConfig.auth.refreshTokenKey, res2.data.refresh);
                const profile2 = await api.get(apiRoutes.auth.user, {
                    headers: { Authorization: `Bearer ${res2.data.access}` },
                });
                localStorage.setItem('user_id', String(profile2.data.id));
                localStorage.setItem('username', profile2.data.username);
                localStorage.setItem('user_profile', JSON.stringify({
                    first_name: profile2.data.first_name || '',
                    last_name: profile2.data.last_name || '',
                    phone: profile2.data.phone || '',
                    email: profile2.data.email || '',
                    national_id: profile2.data.national_id || '',
                    is_agent: profile2.data.is_agent || false,
                }));
                onLoginSuccess();
            } catch {
                setError('نام کاربری یا رمز عبور اشتباه است');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div style={{
                padding: '14px 18px', borderRadius: 12, background: '#fff8e1',
                border: '1px solid #ffe082', color: '#795548',
                fontSize: '0.88rem', marginBottom: 24, lineHeight: 1.7
            }}>
                ⚠️ این کد ملی قبلاً در سیستم ثبت شده است.
                برای ادامه باید با حساب کاربری خود وارد شوید.
                پس از ورود، درخواست شما ثبت می‌شود.
            </div>

            <div className="aaw-field">
                <label className="aaw-label">کد ملی (نام کاربری)</label>
                <input className="aaw-input" value={nationalId} disabled />
            </div>

            <div className="aaw-field">
                <label className="aaw-label">رمز عبور *</label>
                <input
                    className={`aaw-input ${error ? 'error' : ''}`}
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="رمز عبور خود را وارد کنید"
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    autoFocus
                />
                {error && <span className="aaw-field-error">{error}</span>}
            </div>

            <div className="aaw-nav">
                <button className="aaw-btn aaw-btn-secondary" onClick={onBack} disabled={loading}>
                    → بازگشت
                </button>
                <button
                    className="aaw-btn aaw-btn-primary"
                    onClick={handleLogin}
                    disabled={loading}
                >
                    {loading ? 'در حال ورود...' : 'ورود و ادامه ←'}
                </button>
            </div>
        </div>
    );
}

// ── Main Wizard ──────────────────────────────────────────────────────────────
export default function AgentApplicationWizard() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [data, setData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    // shown when backend returns 409 (national_id already exists)
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    const next = () => setStep(s => Math.min(s + 1, TOTAL_STEPS));
    const back = () => setStep(s => Math.max(s - 1, 1));

    const handleDataChange = (valOrFn) => {
        if (typeof valOrFn === 'function') {
            setData(prev => valOrFn(prev));
        } else {
            setData(valOrFn);
        }
    };

    const doSubmit = async () => {
        const fd = new FormData();
        fd.append('full_name', data.full_name || '');
        fd.append('national_id', data.national_id || '');
        fd.append('phone', data.phone || '');
        fd.append('email', data.email || '');
        fd.append('province', data.province || '');
        fd.append('city', data.city || '');
        fd.append('address', data.address || '');
        fd.append('years_of_experience', data.years_of_experience ?? 0);
        fd.append('motivation', data.motivation || '');
        (data.preferred_neighborhoods || []).forEach((n, i) => {
            fd.append(`preferred_neighborhood_ids[${i}]`, n.id);
        });
        (data.documents || []).forEach((doc, i) => {
            fd.append(`documents_payload[${i}].type`, doc.type);
            fd.append(`documents_payload[${i}].label`, doc.label || '');
            fd.append(`documents_payload[${i}].file`, doc.file);
        });
        await submitApplication(fd);
        setSubmitted(true);
    };

    const handleSubmit = async () => {
        setSubmitError('');
        setSubmitting(true);
        try {
            const token = localStorage.getItem(appConfig.auth.accessTokenKey);
            if (!token) {
                // unauthenticated — try to create account
                try {
                    const authResult = await agentApplicantAuth({
                        national_id: data.national_id,
                        full_name: data.full_name,
                        phone: data.phone,
                        email: data.email || '',
                    });
                    localStorage.setItem(appConfig.auth.accessTokenKey, authResult.access);
                    localStorage.setItem(appConfig.auth.refreshTokenKey, authResult.refresh);
                    localStorage.setItem('user_id', String(authResult.user_id));
                    localStorage.setItem('username', authResult.username);
                    localStorage.setItem('user_profile', JSON.stringify({
                        first_name: authResult.first_name,
                        last_name: authResult.last_name,
                        phone: authResult.phone,
                        email: authResult.email,
                        national_id: authResult.national_id,
                        is_agent: false,
                    }));
                } catch (authErr) {
                    // 409 = national_id exists → show login prompt, stop here
                    if (authErr?.response?.status === 409) {
                        setShowLoginPrompt(true);
                        setSubmitting(false);
                        return;
                    }
                    throw authErr;
                }
            }
            await doSubmit();
        } catch (err) {
            const ed = err?.response?.data;
            if (typeof ed === 'string') setSubmitError(ed);
            else if (ed?.detail) setSubmitError(ed.detail);
            else if (typeof ed === 'object') setSubmitError(JSON.stringify(ed));
            else setSubmitError('خطا در ارسال درخواست. لطفاً دوباره تلاش کنید.');
        } finally {
            setSubmitting(false);
        }
    };

    // Called after successful login inside LoginPrompt
    const handleLoginSuccess = async () => {
        setShowLoginPrompt(false);
        setSubmitting(true);
        setSubmitError('');
        try {
            await doSubmit();
        } catch (err) {
            const ed = err?.response?.data;
            if (typeof ed === 'string') setSubmitError(ed);
            else if (ed?.detail) setSubmitError(ed.detail);
            else if (typeof ed === 'object') setSubmitError(JSON.stringify(ed));
            else setSubmitError('خطا در ارسال درخواست. لطفاً دوباره تلاش کنید.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="aaw-page" dir="rtl">
                <div className="aaw-container">
                    <div className="aaw-step-body">
                        <div className="aaw-success">
                            <div className="aaw-success-icon">✓</div>
                            <h2>درخواست شما ثبت شد!</h2>
                            <p>
                                درخواست نمایندگی شما با موفقیت ارسال شد و در صف بررسی قرار گرفت.
                                پس از بررسی توسط تیم سایتا، نتیجه از طریق ایمیل به شما اطلاع داده می‌شود.
                            </p>
                            <button className="aaw-btn aaw-btn-primary" onClick={() => navigate('/')}>
                                بازگشت به صفحه اصلی
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="aaw-page" dir="rtl">
            <div className="aaw-container">
                <header className="aaw-header">
                    <h1 className="aaw-title">ثبت‌نام به عنوان نماینده</h1>
                    <p className="aaw-subtitle">
                        مراحل زیر را تکمیل کنید تا درخواست نمایندگی شما در سامانه سایتا ثبت شود.
                    </p>
                </header>

                <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} labels={STEP_LABELS} />

                <div className="aaw-step-body">
                    {/* Login prompt overlays step 5 when national_id already exists */}
                    {showLoginPrompt ? (
                        <LoginPrompt
                            nationalId={data.national_id}
                            onLoginSuccess={handleLoginSuccess}
                            onBack={() => setShowLoginPrompt(false)}
                        />
                    ) : (
                        <>
                            {step === 1 && (
                                <Step1_PersonalInfo data={data} onChange={handleDataChange} onNext={next} />
                            )}
                            {step === 2 && (
                                <Step2_LocationAndNeighborhoods data={data} onChange={setData} onNext={next} onBack={back} />
                            )}
                            {step === 3 && (
                                <Step3_Experience data={data} onChange={setData} onNext={next} onBack={back} />
                            )}
                            {step === 4 && (
                                <Step4_Documents data={data} onChange={setData} onNext={next} onBack={back} />
                            )}
                            {step === 5 && (
                                <Step5_Review
                                    data={data}
                                    onBack={back}
                                    onSubmit={handleSubmit}
                                    submitting={submitting}
                                    error={submitError}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
